import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import apiClient from '../api/client';
import { THEME } from '../theme/themeColors';

const BRAND       = THEME.primary;
const BRAND_LIGHT = THEME.primaryLight;
const BRAND_DARK  = THEME.primaryHover;
const ON_BRAND    = THEME.onPrimary;

const labelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#495057',
  textTransform: 'uppercase', letterSpacing: '0.3px',
  marginBottom: '3px', display: 'block'
};

const inputStyle = {
  height: '28px', fontSize: '12px', padding: '2px 8px',
  border: '1px solid #dee2e6', borderRadius: '4px', width: '100%',
  outline: 'none',
};

const selectStyle = {
  ...inputStyle,
  backgroundColor: '#fff', cursor: 'pointer',
};

export default function ViewTicketHistoryModal({ ticket, domain, onClose }) {
  const [history,       setHistory]       = useState([]);
  const [documents,     setDocuments]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [uploading,     setUploading]     = useState(false);
  const [posting,       setPosting]       = useState(false);
  const [replyText,     setReplyText]     = useState('');
  const [isInternal,    setIsInternal]    = useState(false);
  const [ccEmail,       setCcEmail]       = useState('');
  const [estimatedDate, setEstimatedDate] = useState(ticket?.dtEstimatedDelivery);
  const [status,        setStatus]        = useState("Support Reply");
  const [subStatus,     setSubStatus]     = useState(ticket?.subStatus);
  const [file,          setFile]          = useState(null);
  const [docName,       setDocName]       = useState('');
  const [activeTab,     setActiveTab]     = useState('history'); // 'history' | 'docs'

  useEffect(() => {
    if (!ticket?.bgTicketId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [histRes, docRes] = await Promise.all([
          apiClient.post(`/api/tickets/history`, { domain, ticketId: ticket.bgTicketId }),
          apiClient.post(`/api/tickets/documents`, { domain, ticketId: ticket.bgTicketId }),
        ]);
        setHistory(histRes.data || []);
        setDocuments(docRes.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load ticket data');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticket, domain]);

  const handlePostReply = async () => {
    if (!replyText.trim()) { toast.error('Please enter reply text'); return; }
    setPosting(true);
    try {
      await apiClient.post(`/api/tickets/thread/add`, {
        domain, ticketId: ticket.bgTicketId,
        description: replyText, isInternal,
        ccEmail: ccEmail.trim() || "",
        estimatedDelivery: estimatedDate || "",
        status: status || "", subStatus: subStatus || "",
      });
      toast.success('Reply posted');
      setReplyText(''); setIsInternal(false); setCcEmail('');
      setEstimatedDate(''); setStatus(''); setSubStatus('');
      const histRes = await apiClient.post(`/api/tickets/history`, { domain, ticketId: ticket.bgTicketId });
      setHistory(histRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post reply');
    } finally { setPosting(false); }
  };

  const handleUploadDocument = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', docName.trim() || file.name);
    formData.append('domain', domain);
    formData.append('ticketId', ticket.bgTicketId);
    try {
      await apiClient.post(`/api/tickets/${ticket.bgTicketId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setFile(null); setDocName('');
      const docRes = await apiClient.post(`/api/tickets/documents`, { domain, ticketId: ticket.bgTicketId });
      setDocuments(docRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const openFile = async (filePath, fileName) => {

    try {
        const res = await apiClient.get(`/api/files/open-path`, {
        params: {
          filePath: (filePath || '').trim(),
          fileName: (fileName || '').trim()
        }
      });
      if (res.data?.url) window.open(res.data.url, '_blank');
      else toast.error('No URL returned');
    } catch (err) {
      console.error("[OPEN ERROR]", err.response?.data || err);
      toast.error(err.response?.data?.message || 'Failed to open file');
    }
  };

  return (
    <>
      <style>{`
        .vth-modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1050;display:flex;align-items:center;justify-content:center; }
        .vth-modal { background:#fff;border-radius:8px;width:min(820px,96vw);max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.18); }
        .vth-header { background:${BRAND};padding:8px 14px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .vth-header h6 { margin:0;color:${ON_BRAND};font-size:13px;font-weight:700;letter-spacing:0.2px; }
        .vth-header .ticket-badge { font-size:11px;color:${ON_BRAND};background:rgba(255,255,255,0.22);padding:2px 10px;border-radius:20px;margin-left:8px; }
        .vth-header .ticket-subject { font-size:11px;color:${ON_BRAND};opacity:0.8;margin-left:8px;max-width:320px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
        .vth-close { background:none;border:none;color:${ON_BRAND};font-size:18px;line-height:1;cursor:pointer;padding:0 2px;opacity:0.85; }
        .vth-close:hover { opacity:1; }
        .vth-body { flex:1;overflow-y:auto;padding:12px 14px; }
        .vth-section { border:1px solid #e9ecef;border-radius:6px;margin-bottom:10px;overflow:hidden; }
        .vth-section-title { font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:${BRAND_DARK};background:${BRAND_LIGHT};padding:5px 10px;border-bottom:1px solid ${THEME.alpha(0.25)}; }
        .vth-section-body { padding:10px; }
        .vth-textarea { width:100%;font-size:12px;padding:6px 8px;border:1px solid #dee2e6;border-radius:4px;resize:vertical;min-height:72px;line-height:1.4;outline:none;font-family:inherit; }
        .vth-textarea:focus { border-color:${BRAND};box-shadow:0 0 0 2px ${BRAND_LIGHT}; }
        .vth-input-focus:focus { border-color:${BRAND} !important;box-shadow:0 0 0 2px ${BRAND_LIGHT} !important; }
        .vth-btn { height:28px;font-size:11px;font-weight:600;padding:0 14px;border-radius:4px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:5px; }
        .vth-btn-primary { background:${BRAND};color:${ON_BRAND}; }
        .vth-btn-primary:hover:not(:disabled) { background:${BRAND_DARK}; }
        .vth-btn-primary:disabled { background:#6c757d;color:#fff;cursor:not-allowed; }
        .vth-btn-outline { background:#fff;color:${BRAND_DARK};border:1px solid ${BRAND}; }
        .vth-btn-outline:hover:not(:disabled) { background:${BRAND_LIGHT}; }
        .vth-btn-outline:disabled { opacity:0.5;cursor:not-allowed; }
        .vth-btn-ghost { background:none;border:none;color:#6c757d;font-size:11px;padding:0 8px;cursor:pointer;height:28px;border-radius:4px; }
        .vth-btn-ghost:hover { background:#f8f9fa; }
        .vth-check-label { font-size:11px;color:#495057;display:flex;align-items:center;gap:5px;cursor:pointer;user-select:none; }
        .vth-tabs { display:flex;gap:1px;border-bottom:1px solid #dee2e6;margin-bottom:10px; }
        .vth-tab { font-size:11px;font-weight:600;padding:5px 14px;border:1px solid transparent;border-bottom:none;border-radius:4px 4px 0 0;cursor:pointer;background:#f8f9fa;color:#6c757d;margin-bottom:-1px; }
        .vth-tab.active { background:#fff;color:${BRAND_DARK};border-color:#dee2e6 #dee2e6 #fff; }
        .vth-history-card { border:1px solid #e9ecef;border-radius:5px;margin-bottom:8px;overflow:hidden; }
        .vth-history-head { background:#f8f9fa;padding:5px 10px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e9ecef; }
        .vth-history-body { padding:8px 10px; }
        .vth-meta { display:grid;grid-template-columns:1fr 1fr;gap:2px 16px;font-size:11px; }
        .vth-meta-label { color:#6c757d;font-weight:600; }
        .vth-meta-val { color:#212529; }
        .vth-desc-box { background:#f8f9fa;border:1px solid #e9ecef;border-radius:4px;padding:7px 9px;font-size:11px;color:#212529;line-height:1.5;margin-top:6px;white-space:pre-wrap;word-break:break-word; }
        .vth-table { width:100%;border-collapse:collapse;font-size:11px; }
        .vth-table th { background:#f8f9fa;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.3px;color:#495057;padding:5px 8px;border-bottom:1px solid #dee2e6; }
        .vth-table td { padding:5px 8px;border-bottom:1px solid #f1f3f4;color:#212529;vertical-align:middle; }
        .vth-table tr:last-child td { border-bottom:none; }
        .vth-table tr:hover td { background:${BRAND_LIGHT}; }
        .vth-file-link { background:none;border:none;color:${BRAND_DARK};font-size:11px;cursor:pointer;padding:0;text-decoration:none; }
        .vth-file-link:hover { text-decoration:underline; }
        .vth-footer { padding:7px 14px;border-top:1px solid #e9ecef;display:flex;justify-content:flex-end;background:#fafafa;flex-shrink:0; }
        .vth-spinner { width:10px;height:10px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:vth-spin 0.7s linear infinite;display:inline-block; }
        .vth-spinner-sm { width:10px;height:10px;border:2px solid ${THEME.alpha(0.3)};border-top-color:${BRAND};border-radius:50%;animation:vth-spin 0.7s linear infinite;display:inline-block; }
        @keyframes vth-spin { to { transform:rotate(360deg); } }
        .vth-empty { text-align:center;padding:20px;font-size:12px;color:#adb5bd; }
        .vth-status-badge { font-size:10px;font-weight:600;padding:1px 7px;border-radius:20px;display:inline-block;background:${BRAND_LIGHT};color:${BRAND_DARK}; }
        .vth-internal-badge { background:#fff3cd;color:#856404;font-size:10px;font-weight:600;padding:1px 7px;border-radius:20px; }
      `}</style>

      <div className="vth-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="vth-modal">

          {/* Header */}
          <div className="vth-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h6>Ticket History</h6>
              {ticket?.szTicketNumber && <span className="ticket-badge">{ticket.szTicketNumber}</span>}
              {ticket?.szSubject && (
                <span className="ticket-subject">— {ticket.szSubject}</span>
              )}
            </div>
            <button className="vth-close" onClick={onClose} aria-label="Close">×</button>
          </div>

          <div className="vth-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '12px' }}>
                <div className="vth-spinner-sm" style={{ width: '20px', height: '20px', margin: '0 auto 8px' }} />
                Loading...
              </div>
            ) : (
              <>
                {/* Reply Section */}
                <div className="vth-section">
                  <div className="vth-section-title">Post Reply</div>
                  <div className="vth-section-body">

                    <textarea
                      className="vth-textarea"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here..."
                    />

                    {/* Row 1: Internal + CC + Est. Delivery */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '8px', alignItems: 'end', marginTop: '7px' }}>
                      <div style={{ paddingBottom: '8px' }}>
                        <label className="vth-check-label">
                          <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} style={{ accentColor: BRAND }} />
                          Internal only
                        </label>
                      </div>
                      <div>
                        <label style={labelStyle}>Email CC</label>
                        <input style={inputStyle} className="vth-input-focus" type="text" value={ccEmail}
                          onChange={(e) => setCcEmail(e.target.value)} placeholder="email@domain.com" />
                      </div>
                      <div>
                        <label style={labelStyle}>Est. Delivery</label>
                        <input style={inputStyle} className="vth-input-focus" type="date" value={estimatedDate}
                          onChange={(e) => setEstimatedDate(e.target.value)} />
                      </div>
                    </div>

                    {/* Row 2: Status + Sub Status + Post button */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end', marginTop: '7px' }}>
                      <div>
                        <label style={labelStyle}>Status</label>
                        <select style={selectStyle} className="vth-input-focus" value={status} onChange={(e) => setStatus(e.target.value)}>
                          <option value="">-- Select --</option>
                          <option value="Open">Open</option>
                          <option value="User Reply">User Reply</option>
                          <option value="Support Reply">Support Reply</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Sub Status</label>
                        <select style={selectStyle} className="vth-input-focus" value={subStatus} onChange={(e) => setSubStatus(e.target.value)}>
                          <option value="">-- Select --</option>
                          <option value="Not Assigned">Not Assigned</option>
                          <option value="Need more information">Need more information</option>
                          <option value="Work in progress">Work in progress</option>
                          <option value="Deferred">Deferred</option>
                          <option value="Waiting on client to close">Waiting on client to close</option>
                          <option value="Developed, but not uploaded">Developed, but not uploaded</option>
                          <option value="Uploaded on server">Uploaded on server</option>
                          <option value="Assigned">Assigned</option>
                          <option value="Put on hold">Put on hold</option>
                          <option value="Auto Closed">Auto Closed</option>
                        </select>
                      </div>
                      <div>
                        <button className="vth-btn vth-btn-primary" onClick={handlePostReply}
                          disabled={!replyText.trim() || posting}>
                          {posting ? <><span className="vth-spinner" />Posting...</> : 'Post Reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="vth-tabs">
                  <button className={`vth-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
                    History ({history.length})
                  </button>
                  <button className={`vth-tab ${activeTab === 'docs' ? 'active' : ''}`} onClick={() => setActiveTab('docs')}>
                    Documents ({documents.length})
                  </button>
                </div>

                {/* History Tab */}
                {activeTab === 'history' && (
                  history.length === 0
                    ? <div className="vth-empty">No history entries yet.</div>
                    : history.map((item, idx) => (
                      <div key={idx} className="vth-history-card">
                        <div className="vth-history-head">
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#212529' }}>
                            {item.dtRaisedOn || '—'}
                          </span>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {item.isInternal && <span className="vth-internal-badge">Internal</span>}
                            {item.szStatus && (
                              <span className="vth-status-badge">{item.szStatus}</span>
                            )}
                            <span style={{ fontSize: '11px', color: '#6c757d' }}>by {item.updatedBy || item.szRaisedBy || '—'}</span>
                          </div>
                        </div>
                        <div className="vth-history-body">
                          <div className="vth-meta">
                            {item.szRaisedBy && <><span className="vth-meta-label">Raised By</span><span className="vth-meta-val">{item.szRaisedBy}</span></>}
                            {item.szDefaultMail && <><span className="vth-meta-label">To</span><span className="vth-meta-val">{item.szDefaultMail}</span></>}
                            {item.szMailCc && <><span className="vth-meta-label">CC</span><span className="vth-meta-val">{item.szMailCc}</span></>}
                            {item.szPriority && <><span className="vth-meta-label">Priority</span><span className="vth-meta-val">{item.szPriority}</span></>}
                          </div>
                          {item.szDescription && (
                            <div className="vth-desc-box">{item.szDescription}</div>
                          )}
                        </div>
                      </div>
                    ))
                )}

                {/* Documents Tab */}
                {activeTab === 'docs' && (
                  <>
                    {/* Upload row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end', marginBottom: '10px' }}>
                      <div>
                        <label style={labelStyle}>Custom Name (optional)</label>
                        <input style={inputStyle} className="vth-input-focus" type="text" value={docName}
                          onChange={(e) => setDocName(e.target.value)} placeholder="Enter filename..." />
                      </div>
                      <div>
                        <label style={labelStyle}>File</label>
                        <input style={{ ...inputStyle, height: 'auto', padding: '3px 6px' }} type="file"
                          onChange={(e) => setFile(e.target.files[0] || null)} />
                      </div>
                      <div>
                        <button className="vth-btn vth-btn-outline" onClick={handleUploadDocument} disabled={!file || uploading}>
                          {uploading ? <><span className="vth-spinner-sm" />Uploading...</> : 'Upload'}
                        </button>
                      </div>
                    </div>

                    {documents.length === 0
                      ? <div className="vth-empty">No documents attached yet.</div>
                      : (
                        <table className="vth-table">
                          <thead>
                            <tr>
                              <th>Name</th><th>File</th><th>Uploaded By</th><th>Uploaded On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documents.map((doc, idx) => (
                              <tr key={idx}>
                                <td>{doc.name || '—'}</td>
                                <td>
                                  <button className="vth-file-link" onClick={() => openFile(doc.filePath, doc.fileName)}>
                                    {doc.fileName}
                                  </button>
                                </td>
                                <td>{doc.uploadedBy || '—'}</td>
                                <td>{doc.uploadedOn || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="vth-footer">
            <button className="vth-btn vth-btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
}